// pages/tasks/useTaskDeletion.ts
import { useCallback, useMemo } from "react";
import { Task } from "@/types";

interface UseTaskDeletionProps {
  selectedTasks: any; // Could be array or object
  newTaskRows: Partial<Task>[];
  setNewTaskRows: (rows: Partial<Task>[]) => void;
  toggleTaskSelection?: (taskId: string) => void;
  deleteTask?: (id: string) => Promise<void>; // ✅ added here
}

export const useTaskDeletion = ({
  selectedTasks,
  newTaskRows,
  setNewTaskRows,
  toggleTaskSelection,
}: UseTaskDeletionProps) => {
  // Calculate selected new tasks count - handle both array and object formats
  const selectedNewTaskCount = useMemo(() => {
    let selectedIds: string[] = [];

    if (Array.isArray(selectedTasks)) {
      // If selectedTasks is an array, use it directly
      selectedIds = selectedTasks;
    } else if (typeof selectedTasks === 'object' && selectedTasks !== null) {
      // If selectedTasks is an object, get keys where value is true
      selectedIds = Object.keys(selectedTasks).filter(id => selectedTasks[id]);
    }    
    const newTaskIds = selectedIds.filter(id => id.startsWith('new-'));
    return newTaskIds.length;
  }, [selectedTasks, newTaskRows]);

  const cancelSelectedTasks = useCallback(async () => {
    try {      
      let selectedTaskIds: string[] = [];

      if (Array.isArray(selectedTasks)) {
        selectedTaskIds = selectedTasks;
      } else if (typeof selectedTasks === 'object' && selectedTasks !== null) {
        selectedTaskIds = Object.keys(selectedTasks).filter(id => selectedTasks[id]);
      }            
      if (selectedTaskIds.length === 0) return;
      
      // Only cancel new tasks (not existing ones)
      const newTaskIds = selectedTaskIds.filter(id => id.startsWith('new-'));
      
      if (newTaskIds.length > 0) {
        // Remove new tasks from local state
        setNewTaskRows(prev => prev.filter(row => !newTaskIds.includes(row.tempId!)));
        
        // Clear selections for new tasks
        newTaskIds.forEach(taskId => {
          if (toggleTaskSelection) {
            toggleTaskSelection(taskId);
          }
        });
              }
      
    } catch (error) {
      console.error("Failed to cancel selected tasks:", error);
    }
  }, [selectedTasks, setNewTaskRows, toggleTaskSelection]);

  // Show delete button only when new tasks are selected
  const showDeleteNewTasks = selectedNewTaskCount > 0;
  return {
    cancelSelectedTasks,
    selectedNewTaskCount,
    showDeleteNewTasks,
  };
};